/**
 * Boot-time validation of Supabase tokens stored in localStorage.
 *
 * The Supabase JS SDK stores its session under a key like `sb-<ref>-auth-token`.
 * If that token is malformed or its refresh_token is expired, the SDK will
 * make a `/token?grant_type=refresh_token` call at boot. When that call
 * times out (we've observed `500: error finding refresh token: timeout`),
 * the entire app hangs until our safety timeout fires, producing the
 * "white screen / spinner forever" behavior reported on refresh.
 *
 * This helper inspects the stored token BEFORE Supabase boots and removes
 * it if it's clearly unusable. The next createClient() call then proceeds
 * with a clean state and the user simply lands on the public route.
 */

import { clearAllAuthStorage } from "@/lib/storage/cleanup";

const SB_TOKEN_PREFIX = "sb-";
const SB_TOKEN_SUFFIX = "-auth-token";

// Project ref of the live Supabase backend. MUST mirror whatever
// src/integrations/supabase/client.ts is currently pointed at — otherwise
// every boot wipes a perfectly valid session as "foreign" and logs the
// user out on hard refresh. We derive it dynamically from the auto-generated
// client URL so the two can never drift apart again.
// Derive from the single sb-<ref>-auth-token key already in storage. This
// guarantees we never classify the only present token as "foreign" (which
// was logging users out on every refresh). If multiple sb-* keys are
// present, the first one wins and the others are wiped as stale.
const deriveCurrentProjectRef = (): string => {
  try {
    if (typeof window === "undefined") return "";
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(SB_TOKEN_PREFIX) && key.endsWith(SB_TOKEN_SUFFIX)) {
        return key.slice(SB_TOKEN_PREFIX.length, key.length - SB_TOKEN_SUFFIX.length);
      }
    }
  } catch {}
  return "";
};
const CURRENT_PROJECT_REF = deriveCurrentProjectRef();

/** Decode the JWT payload safely. Returns null if not a JWT. */
const decodeJwtPayload = (jwt: string): { exp?: number; sub?: string; iss?: string; ref?: string } | null => {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload;
  } catch {
    return null;
  }
};

/**
 * Validate the stored Supabase session.
 * Returns true if the token looks usable; false if we removed it.
 */
export const validateStoredSupabaseSession = (): boolean => {
  if (typeof window === "undefined") return true;

  try {
    const storage = window.localStorage;
    const sbKeys: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(SB_TOKEN_PREFIX) && key.endsWith(SB_TOKEN_SUFFIX)) {
        sbKeys.push(key);
      }
    }

    if (sbKeys.length === 0) return true; // no session, nothing to validate

    // Drop any token key whose project ref does not match the current backend.
    // Without this, an SDK upgrade or a stale token from the previous Supabase
    // project gets picked up and triggers /user with a JWT that has a
    // different `iss` → 403 "invalid claim: missing sub claim" loop.
    const expectedKey = `${SB_TOKEN_PREFIX}${CURRENT_PROJECT_REF}${SB_TOKEN_SUFFIX}`;
    const foreignKeys = sbKeys.filter((k) => k !== expectedKey);
    if (foreignKeys.length > 0) {
      console.warn("[bootCleanup] foreign sb-* tokens found, clearing all auth storage", { foreignKeys });
      clearAllAuthStorage();
      return false;
    }

    const tokenKey = expectedKey;
    const raw = storage.getItem(tokenKey);
    if (!raw) return true;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      console.warn("[bootCleanup] Supabase token is malformed JSON — clearing");
      clearAllAuthStorage();
      return false;
    }

    const session = parsed as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
    };

    // Missing critical fields → clear
    if (!session?.access_token || !session?.refresh_token) {
      console.warn("[bootCleanup] Supabase token missing fields — clearing");
      clearAllAuthStorage();
      return false;
    }

    const payload = decodeJwtPayload(session.access_token);
    if (payload) {
      // Cross-project token: payload.iss / payload.ref points to a different
      // backend. The SDK will fire it at /user and earn a 403. Strip now.
      if (payload.ref && payload.ref !== CURRENT_PROJECT_REF) {
        console.warn("[bootCleanup] JWT ref mismatch — clearing", { tokenRef: payload.ref, expectedRef: CURRENT_PROJECT_REF });
        clearAllAuthStorage();
        return false;
      }
      if (payload.iss && !payload.iss.includes(CURRENT_PROJECT_REF)) {
        console.warn("[bootCleanup] JWT issuer mismatch — clearing", { iss: payload.iss });
        clearAllAuthStorage();
        return false;
      }
      // Tokens without a sub claim are corrupted; the SDK loops on them.
      if (!payload.sub) {
        console.warn("[bootCleanup] JWT missing sub claim — clearing");
        clearAllAuthStorage();
        return false;
      }
      // If access_token is a JWT, check exp. We do NOT clear when only the
      // access_token is expired — the SDK can refresh it. We only clear when
      // the access_token is so old that its refresh window is clearly past
      // (heuristic: > 30 days old).
      if (payload.exp) {
        const ageDays = (Date.now() / 1000 - payload.exp) / 86400;
        if (ageDays > 30) {
          console.warn(
            "[bootCleanup] Supabase token expired > 30d ago — clearing",
            { ageDays: Math.round(ageDays) }
          );
          clearAllAuthStorage();
          return false;
        }
      }
    }

    return true;
  } catch (err) {
    // An unexpected exception here (e.g. localStorage unavailable in private
    // mode, quota exceeded, security policy blocking access) tells us
    // nothing about the validity of the stored token. Wiping the session
    // in this case caused random logouts on private-mode browsers and
    // storage-restricted environments. Leave the token alone and let the
    // Supabase SDK do its own validation — it can refresh, recover, or
    // surface a real auth error to the app.
    console.warn("[bootCleanup] unexpected error during validation, leaving storage intact", err);
    return true;
  }
};
